/**
 * App.tsx — thin shell: providers, auth guards, and router.
 * - Error boundaries: src/components/ErrorBoundaries.tsx
 * - Route definitions: src/routes/*
 * (Refactored in #2470)
 */
import React, { lazy, ReactNode, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { BrandProvider } from "./context/BrandContext";
import { ConfigProvider } from "./context/ConfigContext";
import { SessionMetaProvider } from "./context/SessionMetaContext";
import { TenantProvider } from "./context/TenantContext";
import { ThemeProvider } from "./context/ThemeContext";
import { parseFleetClaims, canViewFleet, canAdminPlatform } from "./lib/fleetRoles";
import { FleetLayout } from "./components/FleetLayout";
import { CookieBanner } from "./components/CookieBanner";
import { StatusBanner } from "./components/StatusBanner"; // #2457
import { SupportChatMount } from "./components/support/SupportChatMount"; // #3105
import { FleetLanding } from "./pages/FleetLanding";
import {
  TopLevelErrorBoundary,
  ChunkLoadErrorBoundary,
} from "./components/ErrorBoundaries";

import {
  getMarketingRoutes,
  getAuthRoutes,
  getHiveRoutes,
  getAccountRoutes,
  getAdminRoutes,
} from "./routes";

// RootDashboard stays here — used by RootEntry which depends on AuthContext
const RootDashboard = lazy(() => import("./pages/RootDashboard").then(m => ({ default: m.RootDashboard })));

// ---------------------------------------------------------------------------
// Shared UI primitives (used by guards below and passed to route modules)
// ---------------------------------------------------------------------------

function PageFallback() {
  return (
    <div style={{ padding: "3rem 1.5rem", color: "var(--brand-text-secondary)", fontFamily: "sans-serif" }}>
      Loading&hellip;
    </div>
  );
}

const PROV_WRAP: React.CSSProperties = {
  display: "flex", justifyContent: "center", alignItems: "center",
  minHeight: "100vh", background: "var(--brand-bg-default)",
};
const PROV_CARD: React.CSSProperties = {
  background: "var(--brand-bg-elevated)", borderRadius: "0.75rem",
  padding: "2rem 2.5rem", maxWidth: 400, width: "90%",
  textAlign: "center", fontFamily: "sans-serif",
};

function ProvisioningBanner() {
  return (
    <div style={PROV_WRAP}>
      <div style={{ ...PROV_CARD, border: "1px solid rgba(95,166,95,0.25)" }}>
        <div style={{ fontSize: "2rem", marginBottom: "0.75rem", lineHeight: 1 }}>&#9881;</div>
        <h2 style={{ color: "var(--brand-status-success)", margin: "0 0 0.5rem", fontSize: "1rem", fontWeight: 600 }}>
          Setting up your account&hellip;
        </h2>
        <p style={{ color: "var(--brand-text-secondary)", fontSize: "0.85rem", margin: 0, opacity: 0.8 }}>
          This takes just a moment. Hang tight.
        </p>
      </div>
    </div>
  );
}

function ProvisioningTimeout() {
  return (
    <div style={PROV_WRAP}>
      <div style={{ ...PROV_CARD, border: "1px solid rgba(201,64,64,0.3)" }}>
        <h2 style={{ color: "var(--brand-status-error)", margin: "0 0 0.5rem", fontSize: "1rem", fontWeight: 600 }}>
          Account setup is taking longer than expected
        </h2>
        <p style={{ color: "var(--brand-text-secondary)", fontSize: "0.85rem", margin: "0 0 1.25rem", opacity: 0.8 }}>
          Refresh to retry, or contact support if this persists.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            background: "rgba(95,166,95,0.15)", border: "1px solid rgba(95,166,95,0.35)",
            color: "var(--brand-status-success)", padding: "0.45rem 1.1rem", borderRadius: "0.4rem",
            cursor: "pointer", fontFamily: "inherit", fontSize: "0.85rem",
          }}
        >
          Refresh
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Auth guards (kept in App.tsx — depend on AuthContext which is declared here)
// ---------------------------------------------------------------------------

/** Redirects unauthenticated users to /login; blocks provisioning states. */
function Protected({ children }: { children: ReactNode }) {
  const { token, logoutReason, profile, profileLoading, provisioningState } = useAuth();
  const claims = parseFleetClaims(token);
  if (!token)                              return <Navigate to="/login" state={{ from: "/fleet/board", reason: logoutReason }} replace />;
  if (profileLoading)                      return <PageFallback />;
  if (provisioningState === "provisioning") return <ProvisioningBanner />;
  if (provisioningState === "timed_out")    return <ProvisioningTimeout />;
  // Merge DDB fleet_role into claims so Google-federated users (no JWT claims) are authorised
  const effectiveClaims = profile?.fleet_role
    ? { ...claims, role: profile.fleet_role }
    : claims;
  if (!canViewFleet(effectiveClaims)) return <Navigate to="/login" replace />;
  return (
    <TenantProvider>
      <SessionMetaProvider>
        <FleetLayout>{children}</FleetLayout>
      </SessionMetaProvider>
    </TenantProvider>
  );
}

/** Platform-admin-only guard — bounces non-admins to /hives.
 *  Backend still enforces the gate independently (defence in depth). */
function PlatformAdminProtected({ children }: { children: ReactNode }) {
  const { token, profile, profileLoading } = useAuth();
  const claims = parseFleetClaims(token);
  if (!token)         return <Navigate to="/login" replace />;
  if (profileLoading) return <PageFallback />;
  const effectiveClaims = profile?.fleet_role
    ? { ...claims, role: profile.fleet_role }
    : claims;
  if (!canAdminPlatform(effectiveClaims)) return <Navigate to="/hives" replace />;
  return (
    <TenantProvider>
      <SessionMetaProvider>
        <FleetLayout>{children}</FleetLayout>
      </SessionMetaProvider>
    </TenantProvider>
  );
}

// Root route at "/" — show the dashboard for signed-in users (inside the
// normal FleetLayout chrome); FleetLanding for everyone else. We can't gate
// the route element with <Protected> alone because that component redirects
// signed-out users to /login, but the brief wants them to land on the
// marketing page (FleetLanding handles the /signup/marketing fork itself).
function RootEntry() {
  const { token } = useAuth();
  if (!token) return <FleetLanding />;
  return (
    <Protected>
      <Suspense fallback={<PageFallback />}>
        <RootDashboard />
      </Suspense>
    </Protected>
  );
}

/** /hives/:projectName/chat redirects to detail with ?chat=1 */
function ChatRedirect() {
  const { projectName } = useParams<{ projectName: string }>();
  return <Navigate to={`/hives/${encodeURIComponent(projectName ?? "")}?chat=1`} replace />;
}

// ---------------------------------------------------------------------------
// Root component
// ---------------------------------------------------------------------------

export function AppFleet() {
  const sharedProps = { PageFallback };
  const guardedProps = { ...sharedProps, Protected, PlatformAdminProtected };

  return (
    <TopLevelErrorBoundary>
    <ThemeProvider>
    <ConfigProvider disableFallback={process.env.NODE_ENV === "production"}>
    <BrandProvider>
    <AuthProvider>
      <BrowserRouter>
        <ChunkLoadErrorBoundary>
        {/* #2457 — service status banner (polls status.2hives.ai every 60s) */}
        <StatusBanner />
        <Routes>
          <Route path="/" element={<RootEntry />} />

          {/* Auth + onboarding — 8 routes */}
          {getAuthRoutes(sharedProps)}

          {/* Marketing + legal + error pages — 22 routes */}
          {getMarketingRoutes(sharedProps)}

          {/* Hive detail + fleet board — 10 routes */}
          {getHiveRoutes({ ...guardedProps, ChatRedirect })}

          {/* Per-user account + profile — 13 routes */}
          {getAccountRoutes(guardedProps)}

          {/* Platform-admin operator routes — 7 routes */}
          {getAdminRoutes(guardedProps)}

          {/* Catch-all: push authenticated users to board */}
          <Route path="*" element={<Navigate to="/fleet/board" replace />} />
        </Routes>
        {/* #2255 — cookie consent banner on every route until user chooses */}
        <CookieBanner />
        {/* #3105 — marketing support FAB (auto-suppresses on FleetLayout routes,
            which expose the drawer trigger instead). Login-required per #3096-1. */}
        <SupportChatMount embed="fab" referrerOrigin="marketing_site" />
        </ChunkLoadErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
    </BrandProvider>
    </ConfigProvider>
    </ThemeProvider>
    </TopLevelErrorBoundary>
  );
}
